import React, { useEffect, useState } from "react";
import { Trans } from "@lingui/react/macro";
import { useLingui } from "@lingui/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/ui/date-picker";
import { format } from "date-fns";
import type { MultiGroupServicePattern } from "@/lib/date/billingPeriods";
import { computeServiceBasedBillingPeriod } from "@/lib/date/billingPeriods";

type BillingOption = "contract-duration" | "service-based" | "custom";

interface BillingDurationCardProps {
  startDate: string;
  endDate?: string;
  billingStartDate?: string;
  billingEndDate?: string;
  hasServices: boolean;
  // Same shape `computeServiceBasedBillingPeriod` consumes: only the
  // group/day/array structure is read, and a parsed contract pattern's
  // `serviceCode` can legitimately be missing.
  servicePattern?: MultiGroupServicePattern;
  billingOption?: "same" | "service_based" | "custom";
  onBillingOptionChange?: (opt: "same" | "service_based" | "custom") => void;
  onBillingDatesChange: (start: string, end?: string) => void;
}

export function BillingDurationCard({
  startDate,
  endDate,
  billingStartDate,
  billingEndDate,
  hasServices,
  servicePattern,
  billingOption,
  onBillingOptionChange,
  onBillingDatesChange,
}: BillingDurationCardProps) {
  const { i18n } = useLingui();
  const startFieldId = React.useId();
  const endFieldId = React.useId();
  const selectedOption = React.useMemo<BillingOption>(() => {
    const mapped: Record<string, BillingOption> = {
      same: "contract-duration",
      service_based: "service-based",
      custom: "custom",
    };
    return billingOption
      ? mapped[billingOption] ?? "contract-duration"
      : "contract-duration";
  }, [billingOption]);
  const [customStartDate, setCustomStartDate] = useState<Date | undefined>();
  const [customEndDate, setCustomEndDate] = useState<Date | undefined>();

  // Convert string dates to Date objects for validation
  const contractStart = startDate ? new Date(startDate) : undefined;
  const contractEnd = endDate ? new Date(endDate) : undefined;

  // Initialize custom dates from props
  useEffect(() => {
    if (billingStartDate) {
      setCustomStartDate(new Date(billingStartDate));
    }
    if (billingEndDate) {
      setCustomEndDate(new Date(billingEndDate));
    }
  }, [billingStartDate, billingEndDate]);

  // Handle option change
  const handleOptionChange = (value: BillingOption) => {
    if (value === selectedOption) return;

    switch (value) {
      case "contract-duration": {
        // Use contract dates
        onBillingDatesChange(startDate, endDate);
        onBillingOptionChange?.("same");
        break;
      }
      case "service-based": {
        // Compute first/last service day within contract range
        const start = startDate ? new Date(startDate) : undefined;
        const end = endDate ? new Date(endDate) : undefined;
        if (start) {
          const { start: s, end: e } = computeServiceBasedBillingPeriod(
            start,
            end,
            servicePattern
          );
          const sStr = s ? format(s, "yyyy-MM-dd") : startDate;
          const eStr = e ? format(e, "yyyy-MM-dd") : endDate;
          onBillingDatesChange(sStr, eStr);
        } else {
          onBillingDatesChange(startDate, endDate);
        }
        onBillingOptionChange?.("service_based");
        break;
      }
      case "custom": {
        // Use custom dates if available, otherwise contract dates
        const customStart = customStartDate
          ? format(customStartDate, "yyyy-MM-dd")
          : startDate;
        const customEnd = customEndDate
          ? format(customEndDate, "yyyy-MM-dd")
          : endDate;
        onBillingDatesChange(customStart, customEnd);
        onBillingOptionChange?.("custom");
        break;
      }
    }
  };

  // Handle custom date changes
  const handleCustomStartChange = (date: Date | undefined) => {
    setCustomStartDate(date);
    if (selectedOption === "custom") {
      const newStart = date ? format(date, "yyyy-MM-dd") : startDate;
      const newEnd = customEndDate
        ? format(customEndDate, "yyyy-MM-dd")
        : endDate;
      onBillingDatesChange(newStart, newEnd);
    }
  };

  const handleCustomEndChange = (date: Date | undefined) => {
    setCustomEndDate(date);
    if (selectedOption === "custom") {
      const newStart = customStartDate
        ? format(customStartDate, "yyyy-MM-dd")
        : startDate;
      const newEnd = date ? format(date, "yyyy-MM-dd") : endDate;
      onBillingDatesChange(newStart, newEnd);
    }
  };

  // Validate that billing dates are within contract duration
  const validateBillingDates = () => {
    if (!contractStart) return true;

    const billingStart =
      selectedOption === "custom" && customStartDate
        ? customStartDate
        : contractStart;
    const billingEnd =
      selectedOption === "custom" && customEndDate
        ? customEndDate
        : contractEnd;

    // Billing start must not be before contract start
    if (billingStart < contractStart) return false;

    // If contract has end date, billing end must not be after contract end
    if (contractEnd && billingEnd && billingEnd > contractEnd) return false;

    return true;
  };

  const isValid = validateBillingDates();

  // Get display dates based on selected option
  const getDisplayDates = () => {
    switch (selectedOption) {
      case "contract-duration":
        return {
          start: contractStart,
          end: contractEnd,
        };
      case "service-based": {
        if (!contractStart) return { start: undefined, end: contractEnd };
        const { start, end } = computeServiceBasedBillingPeriod(
          contractStart,
          contractEnd,
          servicePattern
        );
        return { start, end };
      }
      case "custom":
        return {
          start: customStartDate || contractStart,
          end: customEndDate || contractEnd,
        };
      default:
        return {
          start: contractStart,
          end: contractEnd,
        };
    }
  };

  const displayDates = getDisplayDates();
  const isDateFieldsDisabled = selectedOption !== "custom";

  // Recompute service-based dates when inputs change and we're in service-based mode
  useEffect(() => {
    if (billingOption !== "service_based") return;
    if (!contractStart) return;
    const { start, end } = computeServiceBasedBillingPeriod(
      contractStart,
      contractEnd,
      servicePattern
    );
    const sStr = start ? format(start, "yyyy-MM-dd") : startDate;
    const eStr = end ? format(end, "yyyy-MM-dd") : endDate;
    if (sStr !== billingStartDate || eStr !== billingEndDate) {
      onBillingDatesChange(sStr, eStr);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    billingOption,
    startDate,
    endDate,
    servicePattern,
    billingStartDate,
    billingEndDate,
  ]);

  // Progressive disclosure - only show if hasServices is true
  if (!hasServices) {
    // Still allow configuration even without services, using contract dates
    // to populate defaults
    return (
      <Card className="flex h-full flex-col">
        <CardHeader>
          <CardTitle className="text-base">
            <Trans id="contracts.wizard.billing.title">Billing Period</Trans>
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-1 flex-col gap-4">
          {/* Quick Options */}
          <div>
            <RadioGroup
              value={selectedOption}
              onValueChange={handleOptionChange}
              className="space-y-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem
                  value="contract-duration"
                  id="contract-duration"
                />
                <Label htmlFor="contract-duration" className="cursor-pointer">
                  <Trans id="contracts.wizard.billing.options.contractDuration">
                    Same as Contract Duration
                  </Trans>
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="service-based" id="service-based" />
                <Label htmlFor="service-based" className="cursor-pointer">
                  <Trans id="contracts.wizard.billing.options.serviceBased">
                    Based on First/Last Service
                  </Trans>
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="custom" id="custom" />
                <Label htmlFor="custom" className="cursor-pointer">
                  <Trans id="contracts.wizard.billing.options.custom">
                    Custom
                  </Trans>
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Date Fields */}
          <div className="mt-auto space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor={startFieldId} className="text-sm font-medium mb-2 block">
                  <Trans id="contracts.wizard.billing.startDate">
                    Billing Start
                  </Trans>
                  <span aria-hidden="true" className="ml-1 text-destructive">*</span>
                </Label>
                <DatePicker
                  id={startFieldId}
                  aria-required="true"
                  value={displayDates.start}
                  onChange={handleCustomStartChange}
                  disabled={selectedOption !== "custom"}
                  className={
                    selectedOption !== "custom" ? "opacity-50 bg-muted" : ""
                  }
                  placeholder={i18n._({ id: "contracts.wizard.billing.startDatePlaceholder", message: "Select billing start date" })}
                />
              </div>

              <div>
                <Label htmlFor={endFieldId} className="text-sm font-medium mb-2 block">
                  <Trans id="contracts.wizard.billing.endDate">
                    Billing End
                  </Trans>
                </Label>
                <DatePicker
                  id={endFieldId}
                  value={displayDates.end}
                  onChange={handleCustomEndChange}
                  disabled={selectedOption !== "custom"}
                  className={
                    selectedOption !== "custom" ? "opacity-50 bg-muted" : ""
                  }
                  placeholder={i18n._({ id: "contracts.wizard.billing.endDatePlaceholder", message: "Select billing end date" })}
                />
              </div>
            </div>

            {/* Validation Error */}
            {!isValid && (
              <div className="text-sm text-destructive bg-destructive-muted p-2 rounded">
                <Trans id="contracts.wizard.billing.validation.outsideContract">
                  Billing dates must be within the contract duration
                </Trans>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="flex h-full flex-col">
      <CardHeader>
        <CardTitle className="text-base">
          <Trans id="contracts.wizard.billing.title">Billing Period</Trans>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-4">
        {/* Quick Options */}
        <div>
          <RadioGroup
            value={selectedOption}
            onValueChange={handleOptionChange}
            className="space-y-2"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem
                value="contract-duration"
                id="contract-duration"
              />
              <Label htmlFor="contract-duration" className="cursor-pointer">
                <Trans id="contracts.wizard.billing.options.contractDuration">
                  Same as Contract Duration
                </Trans>
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="service-based" id="service-based" />
              <Label htmlFor="service-based" className="cursor-pointer">
                <Trans id="contracts.wizard.billing.options.serviceBased">
                  Based on First/Last Service
                </Trans>
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="custom" id="custom" />
              <Label htmlFor="custom" className="cursor-pointer">
                <Trans id="contracts.wizard.billing.options.custom">
                  Custom
                </Trans>
              </Label>
            </div>
          </RadioGroup>
        </div>

        {/* Date Fields */}
        <div className="mt-auto space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor={startFieldId} className="text-sm font-medium mb-2 block">
                <Trans id="contracts.wizard.billing.startDate">
                  Billing Start
                </Trans>
                <span aria-hidden="true" className="ml-1 text-destructive">*</span>
              </Label>
              <DatePicker
                id={startFieldId}
                aria-required="true"
                value={displayDates.start}
                onChange={handleCustomStartChange}
                disabled={isDateFieldsDisabled}
                className={isDateFieldsDisabled ? "opacity-50 bg-muted" : ""}
                placeholder={i18n._({ id: "contracts.wizard.billing.startDatePlaceholder", message: "Select billing start date" })}
              />
            </div>

            <div>
              <Label htmlFor={endFieldId} className="text-sm font-medium mb-2 block">
                <Trans id="contracts.wizard.billing.endDate">Billing End</Trans>
              </Label>
              <DatePicker
                id={endFieldId}
                value={displayDates.end}
                onChange={handleCustomEndChange}
                disabled={isDateFieldsDisabled}
                className={isDateFieldsDisabled ? "opacity-50 bg-muted" : ""}
                placeholder={i18n._({ id: "contracts.wizard.billing.endDatePlaceholder", message: "Select billing end date" })}
              />
            </div>
          </div>

          {/* Validation Error */}
          {!isValid && (
            <div className="text-sm text-destructive bg-destructive-muted p-2 rounded">
              <Trans id="contracts.wizard.billing.validation.outsideContract">
                Billing dates must be within the contract duration
              </Trans>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
